import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, CheckCircle2, Cpu, AlertTriangle, Lightbulb, BarChart3, Layers } from 'lucide-react';
import GithubIcon from './icons/GithubIcon';

const ProjectModal = ({ project, isOpen, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !project) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 bg-black/80 backdrop-blur-md transition-opacity"
        />

        {/* Modal Window */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", duration: 0.5, bounce: 0.15 }}
          className="relative w-full max-w-3xl my-8 bg-[#0D0D0D] border border-[#262626] rounded-[28px] sm:rounded-[32px] p-6 sm:p-10 shadow-2xl z-10 text-white max-h-[90vh] overflow-y-auto custom-scrollbar"
        >
          {/* Top Bar with Category Badge & Close Button */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <span className="px-3.5 py-1 text-xs font-semibold uppercase tracking-wider rounded-full bg-white/10 text-white border border-white/20">
                {project.category}
              </span>
              <span className="text-xs text-slate-400 font-mono">Case Study</span>
            </div>

            <button
              onClick={onClose}
              aria-label="Close modal"
              className="w-10 h-10 rounded-full flex items-center justify-center bg-[#1A1A1A] hover:bg-white hover:text-black border border-[#333] text-slate-400 transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {/* Title & Tagline */}
          <h2 className="text-3xl sm:text-4xl font-bold font-display tracking-tight mb-2 bg-clip-text text-transparent bg-gradient-to-r from-white via-slate-100 to-slate-400">
            {project.title}
          </h2>
          <p className="text-slate-400 text-base sm:text-lg font-light mb-8 leading-relaxed">
            {project.tagline || project.description}
          </p>

          {/* Key Metrics Strip */}
          {project.metrics && project.metrics.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mb-8 p-4 rounded-2xl bg-[#141414] border border-[#222]">
              {project.metrics.map((metric, idx) => (
                <div key={idx} className="text-center">
                  <div className="text-lg sm:text-2xl font-bold font-display text-white tracking-tight">
                    {metric.value}
                  </div>
                  <div className="text-[11px] sm:text-xs text-slate-400 font-medium uppercase tracking-wider mt-0.5">
                    {metric.label}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Problem & Solution Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
            <div className="p-5 rounded-2xl bg-[#141414] border border-[#222]">
              <div className="flex items-center gap-2 text-rose-400 text-sm font-semibold mb-2">
                <AlertTriangle size={16} />
                <span>The Challenge</span>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed font-light">
                {project.problem}
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-[#141414] border border-[#222]">
              <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold mb-2">
                <Lightbulb size={16} />
                <span>The Engineered Solution</span>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed font-light">
                {project.solution}
              </p>
            </div>
          </div>

          {/* System Architecture */}
          {project.architecture && (
            <div className="mb-8 p-5 rounded-2xl bg-[#141414] border border-[#222]">
              <div className="flex items-center gap-2 text-sky-400 text-sm font-semibold mb-2">
                <Cpu size={16} />
                <span>System Architecture</span>
              </div>
              <p className="text-slate-300 text-sm leading-relaxed font-light">
                {project.architecture}
              </p>
            </div>
          )}

          {/* Key Features List */}
          {project.keyFeatures && (
            <div className="mb-8">
              <h3 className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-4 flex items-center gap-2">
                <Layers size={14} />
                <span>Key Technical Capabilities</span>
              </h3>
              <div className="space-y-2.5">
                {project.keyFeatures.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3 text-sm text-slate-300">
                    <CheckCircle2 size={16} className="text-emerald-400 shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tech Stack Pills */}
          <div className="mb-10">
            <h3 className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-3">
              Technologies Used
            </h3>
            <div className="flex flex-wrap gap-2">
              {project.tags.map((tag, i) => (
                <span
                  key={i}
                  className="px-3 py-1.5 rounded-xl bg-[#1A1A1A] border border-[#333] text-xs font-medium text-slate-300"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-[#222]">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <a
                href={project.githubLink || "https://github.com/RSanjeyan"}
                target="_blank"
                rel="noreferrer"
                className="flex-1 sm:flex-none px-5 py-3 rounded-xl bg-[#1A1A1A] hover:bg-[#252525] text-white border border-[#333] text-sm font-medium transition-colors flex items-center justify-center gap-2"
              >
                <GithubIcon size={16} />
                Source Code
              </a>
              {project.demoLink && project.demoLink !== "#" ? (
                <a
                  href={project.demoLink}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 sm:flex-none px-5 py-3 rounded-xl bg-white text-black hover:bg-slate-200 text-sm font-bold transition-colors flex items-center justify-center gap-2"
                >
                  <ExternalLink size={16} />
                  Live Preview
                </a>
              ) : null}
            </div>

            <button
              onClick={onClose}
              className="w-full sm:w-auto px-6 py-3 rounded-xl text-slate-400 hover:text-white text-sm font-medium transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ProjectModal;
